import { useMemo, useState } from "react";

import type {
  InventoryHistoryMetric,
  InventoryHistorySeries,
} from "./inventoryOverTimeApi";

const CHART_WIDTH = 960;
const CHART_HEIGHT = 340;
const PADDING = { top: 24, right: 24, bottom: 48, left: 80 };
const SERIES_COLORS = [
  "#2563eb",
  "#059669",
  "#dc2626",
  "#7c3aed",
  "#d97706",
  "#0891b2",
  "#db2777",
  "#4f46e5",
  "#65a30d",
  "#ea580c",
  "#0f766e",
  "#9333ea",
];

interface ChartPoint {
  period: string;
  value: number;
  estimated: boolean;
}

interface ChartSeries {
  key: string;
  label: string;
  points: ChartPoint[];
}

interface HoveredPoint {
  label: string;
  period: string;
  value: number;
  estimated: boolean;
  leftPercent: number;
  topPercent: number;
}

function shortPeriodLabel(period: string): string {
  const parsed = new Date(`${period}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return period;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    year: "2-digit",
  });
}

export function InventoryOverTimeChart({
  periods,
  series,
  metric,
  formatValue,
}: {
  periods: string[];
  series: InventoryHistorySeries[];
  metric: InventoryHistoryMetric;
  formatValue: (value: number) => string;
}) {
  const [hovered, setHovered] = useState<HoveredPoint | null>(null);
  const chartSeries = useMemo<ChartSeries[]>(() => {
    const latestPeriod = periods.at(-1);
    return [...series]
      .sort((left, right) => {
        const leftPoint = left.points.find(
          (point) => point.period === latestPeriod,
        );
        const rightPoint = right.points.find(
          (point) => point.period === latestPeriod,
        );
        return (
          (rightPoint?.[metric] ?? 0) - (leftPoint?.[metric] ?? 0)
        );
      })
      .slice(0, SERIES_COLORS.length)
      .map((entry) => {
        const pointsByPeriod = new Map(
          entry.points.map((point) => [point.period, point]),
        );
        return {
          key: entry.key,
          label: entry.label,
          points: periods.map((period) => {
            const point = pointsByPeriod.get(period);
            return {
              period,
              value: point?.[metric] ?? 0,
              estimated: point?.estimated ?? false,
            };
          }),
        };
      });
  }, [metric, periods, series]);

  if (periods.length === 0 || chartSeries.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-slate-500">
        No inventory history matches these filters.
      </p>
    );
  }

  const maximum = Math.max(
    1,
    ...chartSeries.flatMap((entry) =>
      entry.points.map((point) => Math.max(0, point.value)),
    ),
  );
  const plotWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const xAt = (index: number): number =>
    periods.length <= 1
      ? PADDING.left + plotWidth / 2
      : PADDING.left + (index / (periods.length - 1)) * plotWidth;
  const yAt = (value: number): number =>
    PADDING.top + plotHeight - (Math.max(0, value) / maximum) * plotHeight;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(
    (fraction) => maximum * fraction,
  );
  const estimatedPeriods = new Set(
    chartSeries.flatMap((entry) =>
      entry.points
        .filter((point) => point.estimated)
        .map((point) => point.period),
    ),
  );
  let lastEstimatedIndex = -1;
  for (let index = periods.length - 1; index >= 0; index -= 1) {
    const period = periods[index];
    if (period && estimatedPeriods.has(period)) {
      lastEstimatedIndex = index;
      break;
    }
  }
  const estimatedBandWidth =
    lastEstimatedIndex < 0
      ? 0
      : Math.max(
          0,
          xAt(lastEstimatedIndex) -
            PADDING.left +
            (periods.length > 1 ? plotWidth / (periods.length - 1) / 2 : 0),
        );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-600">
        {chartSeries.map((entry, index) => (
          <span key={entry.key} className="inline-flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: SERIES_COLORS[index] }}
              aria-hidden
            />
            {entry.label}
          </span>
        ))}
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${String(CHART_WIDTH)} ${String(CHART_HEIGHT)}`}
          width="100%"
          height={CHART_HEIGHT}
          role="img"
          aria-label={`Inventory ${metric} over time`}
          onMouseLeave={() => setHovered(null)}
        >
          {estimatedBandWidth > 0 ? (
            <g>
              <rect
                x={PADDING.left}
                y={PADDING.top}
                width={estimatedBandWidth}
                height={plotHeight}
                className="fill-amber-50"
              />
              <text
                x={PADDING.left + 8}
                y={PADDING.top + 14}
                className="fill-amber-700"
                fontSize={11}
              >
                Estimated backfill
              </text>
            </g>
          ) : null}
          {yTicks.map((tick) => {
            const y = yAt(tick);
            return (
              <g key={tick}>
                <line
                  x1={PADDING.left}
                  x2={CHART_WIDTH - PADDING.right}
                  y1={y}
                  y2={y}
                  className="stroke-slate-200"
                />
                <text
                  x={PADDING.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-slate-500"
                  fontSize={11}
                >
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}
          {periods.map((period, index) => {
            const showLabel =
              periods.length <= 12 ||
              index === 0 ||
              index === periods.length - 1 ||
              index % Math.ceil(periods.length / 10) === 0;
            return showLabel ? (
              <text
                key={period}
                x={xAt(index)}
                y={CHART_HEIGHT - 14}
                textAnchor="middle"
                className="fill-slate-500"
                fontSize={11}
              >
                {shortPeriodLabel(period)}
              </text>
            ) : null;
          })}
          {chartSeries.map((entry, seriesIndex) => {
            const path = entry.points
              .map(
                (point, index) =>
                  `${index === 0 ? "M" : "L"} ${xAt(index)} ${yAt(point.value)}`,
              )
              .join(" ");
            return (
              <g key={entry.key}>
                <path
                  d={path}
                  fill="none"
                  stroke={SERIES_COLORS[seriesIndex]}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {entry.points.map((point, pointIndex) => {
                  const x = xAt(pointIndex);
                  const y = yAt(point.value);
                  return (
                    <circle
                      key={`${entry.key}-${point.period}`}
                      cx={x}
                      cy={y}
                      r={4}
                      fill={SERIES_COLORS[seriesIndex]}
                      className="cursor-crosshair"
                      onMouseEnter={() =>
                        setHovered({
                          label: entry.label,
                          period: point.period,
                          value: point.value,
                          estimated: point.estimated,
                          leftPercent: (x / CHART_WIDTH) * 100,
                          topPercent: (y / CHART_HEIGHT) * 100,
                        })
                      }
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>
        {hovered ? (
          <div
            className="pointer-events-none absolute z-20 min-w-40 -translate-x-1/2 -translate-y-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg"
            style={{
              left: `${String(hovered.leftPercent)}%`,
              top: `${String(hovered.topPercent)}%`,
            }}
          >
            <div className="font-medium text-slate-900">{hovered.label}</div>
            <div className="text-slate-600">
              {shortPeriodLabel(hovered.period)}: {formatValue(hovered.value)}
            </div>
            {hovered.estimated ? (
              <div className="mt-1 text-amber-700">Estimated backfill</div>
            ) : null}
          </div>
        ) : null}
      </div>
      {series.length > chartSeries.length ? (
        <p className="text-xs text-slate-500">
          The chart shows the {String(chartSeries.length)} largest current
          series. The table includes every returned series.
        </p>
      ) : null}
    </div>
  );
}
